import React, { createContext, useContext, useState, useCallback, useEffect, useRef, lazy, Suspense } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { devLog } from "../utils/devLog";

const LoginModal = lazy(() => import("../components/LoginModal/LoginModal"));

const LoginModalContext = createContext(null);

export const LoginModalProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [redirectPath, setRedirectPath] = useState(null);
  const [pendingActions, setPendingActions] = useState([]);
  const [initialOtpMode, setInitialOtpMode] = useState("login");
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const wasAuthenticatedRef = useRef(isAuthenticated);
  const awaitingFreshLoginRef = useRef(false);

  // Execute pending actions only after a fresh login — not when modal opens with stale auth
  useEffect(() => {
    const justLoggedIn = !wasAuthenticatedRef.current && isAuthenticated;
    wasAuthenticatedRef.current = isAuthenticated;

    if (!justLoggedIn || pendingActions.length === 0) {
      return;
    }

    awaitingFreshLoginRef.current = false;
    devLog("🔄 User authenticated, executing pending actions:", pendingActions.length);
    
    const executeActions = async () => {
      const actionsToExecute = [...pendingActions];
      devLog("📋 Executing", actionsToExecute.length, "pending action(s)");
      
      // Clear pending actions immediately to prevent re-execution
      setPendingActions([]);
      
      // Brief tick so auth context and localStorage are in sync after login modal closes
      await new Promise(resolve => setTimeout(resolve, 50));
      
      for (let i = 0; i < actionsToExecute.length; i++) {
        const action = actionsToExecute[i];
        try {
          if (typeof action === 'function') {
            devLog(`▶️ Executing pending action ${i + 1}/${actionsToExecute.length}`);
            // Try to execute as async (will work for both sync and async functions)
            const result = action();
            // If it returns a promise, await it
            if (result && typeof result.then === 'function') {
              await result;
              devLog(`✅ Pending action ${i + 1} completed successfully`);
            } else {
              devLog(`✅ Pending action ${i + 1} completed (sync)`);
            }
          }
        } catch (error) {
          console.error(`❌ Error executing pending action ${i + 1}:`, error);
        }
      }
      devLog("🎉 All pending actions executed");
    };
    
    executeActions();
  }, [isAuthenticated, pendingActions]);

  // Also listen for auth-changed event as backup
  useEffect(() => {
    const handleAuthChange = () => {
      // Small delay to ensure auth state is updated
      setTimeout(() => {
        if (isAuthenticated && pendingActions.length > 0) {
          devLog("🔔 Auth-changed event received, checking pending actions");
          // Trigger re-execution by updating a dependency
          // The main useEffect above will handle it
        }
      }, 500);
    };

    window.addEventListener("auth-changed", handleAuthChange);
    return () => {
      window.removeEventListener("auth-changed", handleAuthChange);
    };
  }, [isAuthenticated, pendingActions]);

  const openLoginModal = useCallback((path = null, pendingAction = null, options = {}) => {
    // Store the current path or provided path for redirect after login
    const pathToStore = path || location.pathname + location.search;
    setRedirectPath(pathToStore);
    setInitialOtpMode(options.otpMode === "register" ? "register" : "login");
    
    // Store pending action if provided
    if (pendingAction) {
      devLog("📝 Storing pending action for execution after login");
      awaitingFreshLoginRef.current = true;
      setPendingActions((prev) => {
        const updated = [...prev, pendingAction];
        devLog("📋 Total pending actions:", updated.length);
        return updated;
      });
    }

    setIsOpen(true);
  }, [location]);

  const closeLoginModal = useCallback(({ loginSuccess = false } = {}) => {
    setIsOpen(false);
    setRedirectPath(null);
    if (loginSuccess) {
      // Keep pending actions — LoginModalContext effect runs them after auth state updates
      return;
    }
    awaitingFreshLoginRef.current = false;
    setPendingActions([]);
  }, []);

  return (
    <LoginModalContext.Provider value={{ openLoginModal, closeLoginModal }}>
      {children}
      {isOpen ? (
        <Suspense fallback={null}>
          <LoginModal 
            isOpen={isOpen} 
            onClose={closeLoginModal} 
            redirectPath={redirectPath}
            hasPendingActions={pendingActions.length > 0}
            initialOtpMode={initialOtpMode}
          />
        </Suspense>
      ) : null}
    </LoginModalContext.Provider>
  );
};

export const useLoginModal = () => {
  const context = useContext(LoginModalContext);
  if (!context) {
    throw new Error("useLoginModal must be used within LoginModalProvider");
  }
  return context;
};
